import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { AppState } from 'src/store/store';
import guidesApiService from 'src/api/services/guidesApiService';
import { SubsRoutesState } from './types';
import { notification } from 'antd';
import dayjs from 'dayjs';

const initialState: SubsRoutesState = {
    isLoading: false,
    items: [],
    filter: {}
};

export const loadSubsRoutesAsync = createAsyncThunk('subsRoutes/load', async () => {
    try {
        const data = await guidesApiService.getSubsRoutes();
        return data;
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
            throw error;
        }
        throw new Error();
    }
});

const slice = createSlice({
    name: 'subsRoutes',
    initialState,
    reducers: {
        reset: () => initialState,
        cityStartFilterChanged: (state, action: PayloadAction<string>) => {
            state.filter.cityStart = action.payload;
        },
        cityFinishFilterChanged: (state, action: PayloadAction<string>) => {
            state.filter.cityFinish = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadSubsRoutesAsync.pending, (state) => {
                state.isLoading = true;
                state.items = [];
            })
            .addCase(loadSubsRoutesAsync.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = action.payload.map((dto, index) => ({
                    key: index,
                    cityStart: dto.cityStart,
                    cityFinish: dto.cityFinish,
                    appendix: dto.appendix,
                    fareAmount: Intl.NumberFormat('ru').format(Math.floor(dto.fareAmount / 100)),
                    subsidyAmount: Intl.NumberFormat('ru').format(Math.floor(dto.subsidyAmount / 100)),
                    currency: dto.currency,
                    validityFrom: dayjs(dto.validityFrom).format('DD.MM.YYYY'),
                    validityTo: dayjs(dto.validityTo).format('DD.MM.YYYY'),
                    routePointsNames: dto.routePointsNames ?? ''
                }));
            })
            .addCase(loadSubsRoutesAsync.rejected, (state) => {
                state.isLoading = false;
            });
    }
});

export const { reset, cityStartFilterChanged, cityFinishFilterChanged } = slice.actions;

const selectSubsRoutes = (state: AppState) => state.subsRoutes;

const selectItems = (state: AppState) => selectSubsRoutes(state).items;

export const selectFilter = (state: AppState) => selectSubsRoutes(state).filter;

export const selectFilteredItems = createSelector([selectItems, selectFilter], (items, filter) => {
    let result = items;
    if (filter.cityStart) {
        result = result.filter((item) => item.cityStart.toLowerCase().includes(filter.cityStart!.toLowerCase()));
    }
    if (filter.cityFinish) {
        result = result.filter((item) => item.cityFinish.toLowerCase().includes(filter.cityFinish!.toLowerCase()));
    }
    return result;
});

export const selectIsLoading = (state: AppState) => selectSubsRoutes(state).isLoading;

export default slice.reducer;
